import { Injectable, OnDestroy } from '@angular/core';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus, InteractionRequiredAuthError, SilentRequest, BrowserAuthError, BrowserAuthErrorCodes } from '@azure/msal-browser';
import { Subject, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MainStore } from '../common/main.store';

@Injectable({
    providedIn: 'root'
  })
export class AuthService implements OnDestroy
{
    private readonly _destroying$ = new Subject<void>();

    private readonly tokenRequest: SilentRequest = { scopes: ['User.Read'] };

    private redirectHandled?: Promise<void>;
    private tokenInFlight?: Promise<string>;

    constructor(
        private msalService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private store: MainStore
    ) { }

    /**
     * True when an MSAL account is available. Waits for any redirect/login interaction to finish first,
     * so a guard that runs while MSAL is still consuming a `#code=` fragment does not see an empty cache.
     */
    async userLoggedIn(): Promise<boolean> {
      await this.waitForInteractionToFinish();
      return this.ensureActiveAccount() !== null;
    }

     ngOnDestroy(): void {
        this._destroying$.next();
        this._destroying$.complete();
    }

    /**
     * Access token for the API. Single-flight: concurrent requests (a dashboard fires a dozen at once)
     * share one MSAL call, so a renewal failure triggers exactly one redirect instead of a race where
     * every caller after the first fails with `interaction_in_progress`.
     */
    getAccessToken(): Promise<string> {
      this.tokenInFlight ??= this.acquireToken().finally(() => { this.tokenInFlight = undefined; });
      return this.tokenInFlight;
    }

    /**
     * When silent renewal is impossible (SPA refresh token past its 24h lifetime, or the hidden-iframe
     * renewal blocked by third-party-cookie rules) we re-authenticate against the existing Microsoft
     * session with a redirect — never log out. The redirect leaves the page, so the returned promise
     * intentionally never settles in that case.
     */
    private async acquireToken(): Promise<string> {
        await this.waitForInteractionToFinish();
        const account = this.ensureActiveAccount();
        if (!account) {
          return this.redirectForToken();
        }
        try {
          const result = await this.msalService.instance.acquireTokenSilent({ ...this.tokenRequest, account });
          return result.accessToken;
        } catch (error) {
          if (AuthService.needsInteraction(error)) {
            return this.redirectForToken(account);
          }
          throw error;
        }
    }

    private async redirectForToken(account?: AccountInfo): Promise<never> {
      try {
        await this.msalService.instance.acquireTokenRedirect({ ...this.tokenRequest, account });
      } catch (error) {
        // Another redirect is already leaving the page; let it.
        if (!(error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.interactionInProgress)) {
          throw error;
        }
      }
      return new Promise<never>(() => { });
    }

    /**
     * Errors where the only way forward is to re-authenticate: the server says so, or the hidden-iframe
     * renewal never came back (third-party cookies blocked, or the iframe simply timed out). Anything else
     * (interaction already in progress, config errors) is a real error and must surface.
     */
    private static needsInteraction(error: unknown): boolean {
      if (error instanceof InteractionRequiredAuthError) {
        return true;
      }
      if (error instanceof BrowserAuthError) {
        return [
          BrowserAuthErrorCodes.timedOut,
          BrowserAuthErrorCodes.emptyWindowError,
          BrowserAuthErrorCodes.noAccountError,
        ].includes(error.errorCode);
      }
      return false;
    }

    public logout() {
      this.store.currentUser.clear();
      this.msalService.logoutRedirect();
    }

    private ensureActiveAccount() {
      const instance = this.msalService.instance;
      let activeAccount = instance.getActiveAccount();
      if (!activeAccount) {
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          instance.setActiveAccount(accounts[0]);
          activeAccount = accounts[0];
        }
      }
      return activeAccount;
    }

    /**
     * `inProgress$` only leaves `Startup` once MSAL has processed the redirect response (or found none),
     * and with no MsalRedirectComponent bootstrapped nobody triggers that for us — so kick it off here,
     * once; msal-browser caches the promise, so later callers (MsalGuard) just reuse it.
     */
    private waitForInteractionToFinish(): Promise<InteractionStatus> {
      this.redirectHandled ??= firstValueFrom(this.msalService.handleRedirectObservable())
        .then(() => undefined, () => undefined);
      return this.redirectHandled.then(() => firstValueFrom(
        this.msalBroadcastService.inProgress$.pipe(
          filter((status: InteractionStatus) => status === InteractionStatus.None)
        )
      ));
    }

}
